import React, { useState } from 'react';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { Download, Upload, Save } from '@mui/icons-material';
import { Translate } from 'react-localize-redux';
import PriceListGrid from './PriceListGrid';
import InsurerAssignmentGrid from './InsurerAssignmentGrid';
import { PriceGridRow } from './priceListGridModel';
import { AssignmentRow } from './insurerAssignmentGridModel';

/**
 * SHER-1128 (SHER-308 Phase 4) — the Billing configuration screen, two tabs (Decision D).
 *
 * The price lists and the insurer→list assignments are two different things to edit, so they
 * get a tab each, and EACH tab owns its own Download / Upload / Save trio against its own CSV:
 * the Prices tab ↔ price-lists.csv, the Assignments tab ↔ assignments.csv (the two files Phase 3
 * defined). Reuses the existing billables Save/Export/Import copy.
 *
 * Presentational layout: it hosts the two grids and the two toolbars. The Billing route wires
 * the toolbar actions to the per-list and assignments services afterwards (component-first:
 * catalog before wiring), so the handlers here are optional and default to no-ops.
 */
export interface BillablesConfigurationTabsProps {
    priceLists: string[];
    priceRows: PriceGridRow[];
    assignmentRows: AssignmentRow[];
    onPriceRowsChange?: (rows: PriceGridRow[]) => void;
    onPriceListsChange?: (priceLists: string[]) => void;
    onAssignmentRowsChange?: (rows: AssignmentRow[]) => void;
    onSavePrices?: () => void;
    onExportPrices?: () => void;
    onImportPrices?: (file: File) => void;
    onSaveAssignments?: () => void;
    onExportAssignments?: () => void;
    onImportAssignments?: (file: File) => void;
}

interface CsvToolbarProps {
    testId: string;
    fileName: string;
    onSave?: () => void;
    onExport?: () => void;
    onImport?: (file: File) => void;
}

/** The Download / Upload / Save trio one tab carries, labelled with the CSV it round-trips. */
const CsvToolbar: React.FC<CsvToolbarProps> = ({ testId, fileName, onSave, onExport, onImport }) => (
    <Box data-testid={testId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" color="primary" startIcon={<Save />} onClick={() => onSave?.()}>
            <Translate id="hospital.billing.billables.save" />
        </Button>
        <Button variant="outlined" startIcon={<Download />} onClick={() => onExport?.()}>
            <Translate id="hospital.billing.billables.export" />
        </Button>
        <Button variant="outlined" component="label" startIcon={<Upload />}>
            <Translate id="hospital.billing.billables.import" />
            <input
                type="file"
                hidden
                accept=".csv"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onImport?.(file);
                    e.target.value = '';
                }}
            />
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {fileName}
        </Typography>
    </Box>
);

const BillablesConfigurationTabs: React.FC<BillablesConfigurationTabsProps> = ({
    priceLists,
    priceRows,
    assignmentRows,
    onPriceRowsChange,
    onPriceListsChange,
    onAssignmentRowsChange,
    onSavePrices,
    onExportPrices,
    onImportPrices,
    onSaveAssignments,
    onExportAssignments,
    onImportAssignments,
}) => {
    const [tab, setTab] = useState(0);

    return (
        <Box sx={{ p: 2 }}>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)} aria-label="billables configuration tabs">
                <Tab label={<Translate id="hospital.billing.billables.tab.prices" />} id="billables-tab-0" />
                <Tab label={<Translate id="hospital.billing.billables.tab.assignments" />} id="billables-tab-1" />
            </Tabs>

            <Box role="tabpanel" hidden={tab !== 0} sx={{ pt: 2 }}>
                {tab === 0 && (
                    <>
                        <CsvToolbar
                            testId="prices-toolbar"
                            fileName="price-lists.csv"
                            onSave={onSavePrices}
                            onExport={onExportPrices}
                            onImport={onImportPrices}
                        />
                        <PriceListGrid
                            priceLists={priceLists}
                            rows={priceRows}
                            onRowsChange={onPriceRowsChange}
                            onPriceListsChange={onPriceListsChange}
                        />
                    </>
                )}
            </Box>

            <Box role="tabpanel" hidden={tab !== 1} sx={{ pt: 2 }}>
                {tab === 1 && (
                    <>
                        <CsvToolbar
                            testId="assignments-toolbar"
                            fileName="assignments.csv"
                            onSave={onSaveAssignments}
                            onExport={onExportAssignments}
                            onImport={onImportAssignments}
                        />
                        <InsurerAssignmentGrid
                            rows={assignmentRows}
                            onRowsChange={onAssignmentRowsChange}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
};

export default BillablesConfigurationTabs;
